// Client TypeScript interfaces for the agent invitation push frames defined in
// docs/blueprint/realtime.md. The frame schema is shared across the browser and
// agent transports so client handlers depend on one wire contract.
//
// Field order is preserved to match the server Go struct:
//   pending : invitation_id, requester_user_id, agent_id, channel_id,
//             created_at, expires_at
//   decided : invitation_id, state, decided_at
//
// Out of scope here: server push implementation, BPP frame envelope. The
// dispatcher consumes these via useWebSocket's existing `data.type`
// switch — see hooks/useWsHubFrames.ts for the listener side.

/**
 * `agent_invitation_pending` — owner receives a notification that another
 * user requested adding the owner's agent to a channel. Replaces the 60s
 * polling on the bell badge per G2.4 latency requirement (latency ≤ 3s).
 */
export interface AgentInvitationPendingFrame {
  type: 'agent_invitation_pending';
  invitation_id: string;
  requester_user_id: string;
  agent_id: string;
  channel_id: string;
  /** Unix ms. */
  created_at: number;
  /** Unix ms. */
  expires_at: number;
}

/**
 * `agent_invitation_decided` — synchronizes invitation state across clients
 * when the owner approves/rejects elsewhere, or the server marks it expired.
 */
export interface AgentInvitationDecidedFrame {
  type: 'agent_invitation_decided';
  invitation_id: string;
  state: 'approved' | 'rejected' | 'expired';
  /** Unix ms. */
  decided_at: number;
}

/** Union of all invitation frames. */
export type AgentInvitationFrame = AgentInvitationPendingFrame | AgentInvitationDecidedFrame;

/**
 * Window-level CustomEvent names fired by useWsHubFrames after a frame
 * lands. Components (Sidebar bell, InvitationsInbox) listen for these
 * to remove their polling code.
 *
 * Naming follows the existing `commands_updated` precedent in
 * useWebSocket — namespace prefix `borgee:` to avoid conflicts with native
 * browser events.
 */
export const INVITATION_PENDING_EVENT = 'borgee:invitation-pending';
export const INVITATION_DECIDED_EVENT = 'borgee:invitation-decided';

/** Strongly-typed CustomEvent payload helpers. */
export type InvitationPendingEvent = CustomEvent<AgentInvitationPendingFrame>;
export type InvitationDecidedEvent = CustomEvent<AgentInvitationDecidedFrame>;

/** A move eviction scopes canonical resource identities to their former Server. */
export interface ChannelMoveSourceEvictFrame {
  type: 'channel_move_source_evict';
  source_guild_id: string;
  subtree_channel_ids: string[];
}
/** A target refresh contains no moved resource identity. */
export interface ChannelMoveTargetRefreshFrame {
  type: 'channel_move_target_refresh';
  target_guild_id: string;
}

/** A structural invalidation discloses no Zone or child metadata. */
export interface ZonesChangedFrame {
  type: 'zones_changed';
  guild_id: string;
}

/** A content-free hint requiring an authoritative Guild and roster reread. */
export interface GuildMembersChangedFrame {
  type: 'guild_members_changed';
  guild_id: string;
}
// ─── MentionPushed frame ────────────────────────────────────
//
// Server lock: internal/ws/mention_pushed_frame.go::MentionPushedFrame.
// The exact field order is:
//   {type, cursor, message_id, channel_id, sender_id,
//    mention_target_id, body_preview, created_at}
// body_preview is rune-truncated to 80 chars server-side
// (TruncateBodyPreview); client must NOT re-parse it (display exactly as
// provided, server data-trim constraint).

/**
 * `mention_pushed` — server → client push fired when a message body
 * `@<target_user_id>` token resolves to an online target. Target-only
 * BroadcastToUser (owner is not copied; offline owner fallback uses
 * a system DM, not this envelope). MessageList listens via
 * useMentionPushed → actions.loadMessages to trigger a rerender.
 */
export interface MentionPushedFrame {
  type: 'mention_pushed';
  /** Monotonic server cursor; client must NOT sort by created_at. */
  cursor: number;
  message_id: string;
  channel_id: string;
  sender_id: string;
  mention_target_id: string;
  /** Server-truncated to 80 runes (UTF-8 rune-safe). Rule ②: do not re-parse. */
  body_preview: string;
  /** Unix ms — display only, not a sort key (server cursor is the authority). */
  created_at: number;
}

export const MENTION_PUSHED_EVENT = 'borgee:mention-pushed';
export type MentionPushedEvent = CustomEvent<MentionPushedFrame>;

// ─── AgentActivity frame ────────────────────────────────
//
// Server lock: packages/server-go/internal/core/domain/pubsub/activity.go
//   ActivityNotification, prefixed by the `type` discriminator. Field order
//   below mirrors that struct.
//
// The frame carries no `cursor`, and must not grow one: a cursor is a position
// in the one sequence the server process mints in memory, and this rail keeps
// nothing a position could be used to catch up from. `sequence` is the producing
// agent's own counter, used only to drop anything not newer.

/** The Agent Client Protocol's ten tool kinds. Treated as open: an unrecognised value renders generically. */
export type AgentActivityKind =
  | 'read'
  | 'edit'
  | 'delete'
  | 'move'
  | 'search'
  | 'execute'
  | 'think'
  | 'fetch'
  | 'switch_mode'
  | 'other';

/** The protocol's four statuses plus the one it lacks: stopped, waiting on a human. */
export type AgentActivityStatus = 'pending' | 'in_progress' | 'waiting' | 'completed' | 'failed';

/** One line of a plan. The protocol gives entries no identity, so they reconcile by position. */
export interface AgentActivityPlanEntry {
  label: string;
  status: 'pending' | 'in_progress' | 'completed';
}

/**
 * `agent_activity` — what an agent is doing inside a turn, delivered to
 * everyone who may read the channel and written nowhere.
 *
 * `shape` says which fields carry meaning: `activity` for one tool call,
 * `plan` for the whole plan, `output` for the tail of a stream, `turn` for the
 * boundary that brackets them. The boundary rides this frame so that it shares
 * `sequence` with the shapes it brackets: a turn's end must never overtake the
 * last activity of that turn.
 */
/** Durable-binding lifecycle event; task_id lets card UI distinguish one agent's work across tasks. */
export interface AgentTaskStateChangedFrame {
  type: 'agent_task_state_changed';
  cursor: number;
  agent_id: string;
  task_id: string;
  state: 'busy' | 'idle';
  subject: string;
  reason: string;
  changed_at: number;
}

export interface AgentActivityFrame {
  type: 'agent_activity';
  channel_id: string;
  /** Stamped by the server from the reporting connection, never from the agent's own frame. */
  agent_id: string;
  /** The producer's monotonic counter. Not a position in any durable sequence. */
  sequence: number;
  shape: 'activity' | 'plan' | 'output' | 'turn' | 'background_run' | 'normalized';

  /** shape='activity': the producer's id. The same id patches the same entry. */
  activity_id?: string;
  /** The protocol's title. For a shell tool this is the raw command line. */
  label?: string;
  /** The provider's own phrasing for the same call. Travels beside the label, not instead of it. */
  description?: string;
  kind?: AgentActivityKind;
  status?: AgentActivityStatus;
  /** One bounded line: the command, the file, the query. Never raw output. */
  detail?: string;
  /** Replaces the previous list rather than adding to it. */
  paths?: string[];
  /** The activity that spawned this activity or background run. */
  parent_id?: string;
  /** A container for other work rather than work of its own. Stated, never inferred from having children. */
  subagent?: boolean;
  /** Provider-recognized background work; not a foreground request for input. */
  background?: boolean;
  background_state?: 'starting' | 'running' | 'waiting' | 'completed' | 'failed';
  follow_up_control?: 'same-session-required' | 'available' | 'unavailable';
  /** Why a terminal activity or background run ended as it did. An open set upstream, so never narrowed to a union. */
  reason?: string;

  /** shape='plan': the whole list, every time. */
  plan?: AgentActivityPlanEntry[];

  /** shape='output': the absolute tail of one stream, not a delta. */
  stream?: 'answer' | 'reasoning';
  text?: string;

  /** shape='turn': which edge this is. Whether an agent is working is what it said, never what its entries imply. */
  turn_state?: 'started' | 'ended';

  run_id?: string;
  run_state?: BackgroundRunState;
  started_at?: number;
  finished_at?: number;
  stop_supported?: boolean;
  stop_unsupported_reason?: string;

  occurred_at?: number;
  protocol_version?: string;
  task_id?: string;
  event?: unknown;
}

export type { NormalizedActivityFrame } from '@borgee/remote-control-client';

export type BackgroundRunState =
  'running' | 'waiting' | 'stopping' | 'completed' | 'failed' | 'cancelled' | 'unknown';

export interface InteractionResponseFrame {
  binding_id?: string;
  native_session_id?: string;
  connection_generation?: string;
  type: 'interaction_response';
  client_request_id: string;
  channel_id: string;
  task_id: string;
  body: Omit<
    import('@borgee/agent-remote-protocol').InteractionResponseMessage,
    'protocolVersion'
  > & { protocolVersion: import('@borgee/agent-remote-protocol').NormalizedProtocolVersion };
}

export type InteractionResponseResultFrame = {
  type: 'interaction_response_result';
  client_request_id: string;
} & (
  | { data: import('@borgee/agent-remote-protocol').InteractionResolvedMessage; error?: never }
  | { data?: never; error: { code: string; message: string } }
);
